// David Walsh's debouncer
export function debounce(func, wait, immediate) {
  let timeout
  return function() {
    let context = this
    let args = arguments
    let later = function() {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    let callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}


export function getBreakpoints(element) {
  // Get breakpoints already declared in CSS, to avoid declaring the same values
  // in numerous different places. Mostly convenient with SCSS or LESS breakpoint
  // variables. To use, set up the element’s CSS as follows:
  //
  // - Add ::before or ::after to a relevant element of your choice.
  //
  // - Set these properties on the pseudo-element to hide it:
  //
  //   display: none;
  //   visibility: hidden;
  //
  // - Set the pseudo-element’s content property like the following, where the
  //   string begins with `bp:` and the breakpoint values are separated with a
  //   comma. Including whitespace is fine, it’ll get stripped out if it’s there.
  //   Each breakpoint value must include a unit of some kind (rem, em, px).
  //
  //   content: 'bp: [small value], [large value]';

  let beforeContent = getComputedStyle(element, '::before').content
  let afterContent = getComputedStyle(element, '::after').content

  let bpStringMatch = /bp:/
  let useBefore = beforeContent.match(bpStringMatch)
  let useAfter = afterContent.match(bpStringMatch)

  if (!useBefore && !useAfter) {
    throw new Error('No breakpoint values found')
  }

  let bpString = useBefore ? beforeContent : afterContent
  let bpValues = bpString.replace(/"|\s|bp:/g, '').split(',')

  return {
    sm: bpValues[0],
    lg: bpValues[1]
  }
}


export function getFocusableEls(parentElement) {
  // From Kitty Giraudel's a11y-dialog
  // https://github.com/KittyGiraudel/a11y-dialog/tree/8.0.0

  const notInert = ':not([inert]):not([inert] *)'
  const notNegTabIndex = ':not([tabindex^="-"])'
  const notDisabled = ':not(:disabled)'

  const focusableSelectors = [
    `a[href]${notInert}${notNegTabIndex}`,
    `area[href]${notInert}${notNegTabIndex}`,
    `input:not([type="hidden"]):not([type="radio"])${notInert}${notNegTabIndex}${notDisabled}`,
    `input[type="radio"]${notInert}${notNegTabIndex}${notDisabled}`,
    `select${notInert}${notNegTabIndex}${notDisabled}`,
    `textarea${notInert}${notNegTabIndex}${notDisabled}`,
    `button${notInert}${notNegTabIndex}${notDisabled}`,
    `details${notInert} > summary:first-of-type${notNegTabIndex}`,
    `iframe${notInert}${notNegTabIndex}`,
    `audio[controls]${notInert}${notNegTabIndex}`,
    `video[controls]${notInert}${notNegTabIndex}`,
    `[contenteditable]${notInert}${notNegTabIndex}`,
    `[tabindex]${notInert}${notNegTabIndex}`,
  ]

  const focusableChildren = parentElement.querySelectorAll(focusableSelectors.join(','))
  return Array.prototype.slice.call(focusableChildren)
}


export function getTransitionDuration(element, pseudoElement = '') {
  let inSeconds = /\ds$/;
  let inMilliseconds = /\dms$/;
  let durationValue =
    window
      .getComputedStyle(element, pseudoElement)
      .getPropertyValue('transition-duration')

  let ds = durationValue.replace(/[^0-9]$/, '')

  if (ds === '0') {
    console.warn('getTransitionDuration found a transition-duration value of 0 — check CSS declaration is set properly')
  }
  if (!durationValue.match(inMilliseconds) && !durationValue.match(inSeconds)) {
    console.warn('getTransitionDuration failed: make sure that CSS transition is declared in seconds (s) or milliseconds (ms)')
  }

  return durationValue.match(inMilliseconds) ? parseFloat(ds) : parseFloat(ds) * 1000
}


export function isHidden(element) {
  // Browsers hide all non-<summary> descendants of closed <details> elements
  // from user interaction, but those non-<summary> elements may still match our
  // focusable-selectors and may still have dimensions, so we need a special
  // case to ignore them.
  if (element.matches('details:not([open]) *') &&
    !element.matches('details>summary:first-of-type'))
    return true
  // If this element has no painted dimensions, it's hidden.
  return !(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
}


export function isInViewport(element) {
  const rect = element.getBoundingClientRect()
  return (
    rect.top >= 0
    && rect.left >= 0
    && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}


export function isObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}


export function isVisible(element) {
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
}


// https://observablehq.com/@mbostock/localized-number-parsing#NumberParser
export class NumberParser {
  constructor(locale) {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    const numerals = [...new Intl.NumberFormat(locale, {useGrouping: false}).format(9876543210)].reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));
    this._group = new RegExp(`[${parts.find(d => d.type === "group").value}]`, "g");
    this._decimal = new RegExp(`[${parts.find(d => d.type === "decimal").value}]`);
    this._numeral = new RegExp(`[${numerals.join("")}]`, "g");
    this._index = d => index.get(d);
  }
  parse(string) {
    return (string = string.trim()
      .replace(this._group, "")
      .replace(this._decimal, ".")
      .replace(this._numeral, this._index)) ? +string : NaN;
  }
}


export function viewportSize(sm, lg) {
  const VIEWPORT_IS_SM = window.matchMedia(`(max-width: ${sm})`)
  const VIEWPORT_IS_LG = window.matchMedia(`(min-width: ${lg})`)

  return {
    isSm: VIEWPORT_IS_SM,
    isLg: VIEWPORT_IS_LG
  }
}
